/**
 * OS-level notification utilities using the Web Notifications API.
 *
 * Features:
 * - Request permission for browser notifications
 * - Show OS notifications for chat messages when tab not focused
 * - Click notification to focus the app
 * - Respects OS notification settings (Do Not Disturb, etc.)
 * - Rate limiting to prevent spam
 * - Privacy mode to hide message content
 */

export type NotificationPermissionStatus = 'granted' | 'denied' | 'default';

// Rate limiting: Track last notification time per conversation
const lastNotificationTime = new Map<string, number>();
const NOTIFICATION_COOLDOWN_MS = 10000; // 10 seconds between notifications per conversation

// Privacy setting - stored in localStorage
const PRIVACY_KEY = 'chat-notification-privacy';
// User preference to disable notifications even if permission granted
const DISABLED_KEY = 'chat-notification-disabled';

/**
 * Get privacy mode setting (hide message content)
 */
export function getNotificationPrivacyMode(): boolean {
  return localStorage.getItem(PRIVACY_KEY) === 'true';
}

/**
 * Set privacy mode (hide message content in notifications)
 */
export function setNotificationPrivacyMode(enabled: boolean): void {
  localStorage.setItem(PRIVACY_KEY, String(enabled));
}

/**
 * Get user preference for notifications (even if permission granted)
 */
export function getNotificationsEnabled(): boolean {
  return localStorage.getItem(DISABLED_KEY) !== 'true';
}

/**
 * Set user preference to enable/disable notifications
 */
export function setNotificationsEnabled(enabled: boolean): void {
  localStorage.setItem(DISABLED_KEY, String(!enabled));
}

/**
 * Check if the browser supports notifications
 */
export function isNotificationSupported(): boolean {
  return 'Notification' in window;
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission(): NotificationPermissionStatus {
  if (!isNotificationSupported()) return 'denied';
  return Notification.permission as NotificationPermissionStatus;
}

/**
 * Request permission to show OS notifications.
 * Returns the permission status after the user responds.
 */
export async function requestNotificationPermission(): Promise<NotificationPermissionStatus> {
  if (!isNotificationSupported()) {
    console.warn('Notifications not supported in this browser');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  // Request permission
  const permission = await Notification.requestPermission();
  return permission as NotificationPermissionStatus;
}

export interface ChatNotificationOptions {
  senderName: string;
  message: string;
  avatarUrl?: string | null;
  conversationId: string;
  isDM: boolean; // Is this a direct message (vs group chat)
  onClickCallback?: () => void;
}

/**
 * Check if we should show a notification based on rate limiting
 */
function canShowNotification(conversationId: string): boolean {
  const now = Date.now();
  const lastTime = lastNotificationTime.get(conversationId);
  
  if (lastTime && now - lastTime < NOTIFICATION_COOLDOWN_MS) {
    return false; // Still in cooldown period
  }
  
  lastNotificationTime.set(conversationId, now);
  return true;
}

/**
 * Show an OS notification for a chat message.
 * Only shows if:
 * - Permission is granted
 * - User hasn't disabled notifications
 * - Tab is not focused
 * - Not in cooldown period for this conversation
 */
export function showChatNotification(options: ChatNotificationOptions): Notification | null {
  const { senderName, message, avatarUrl, conversationId, isDM, onClickCallback } = options;

  // Check permission
  if (getNotificationPermission() !== 'granted') {
    return null;
  }

  // Check if user has disabled notifications
  if (!getNotificationsEnabled()) {
    return null;
  }

  // Only show if tab is not focused (user is in another app/tab)
  if (document.hasFocus()) {
    return null;
  }

  // Rate limiting: Don't spam notifications
  if (!canShowNotification(conversationId)) {
    return null;
  }

  // Privacy mode: Hide message content if enabled
  const privacyMode = getNotificationPrivacyMode();
  const body = privacyMode 
    ? (isDM ? 'Sent you a message' : 'New message in group chat')
    : message;

  // Create notification
  const notification = new Notification(senderName, {
    body,
    icon: avatarUrl || '/logo.png', // Use avatar or BandMate logo
    badge: '/logo.png', // Small monochrome icon for mobile
    tag: `chat-${conversationId}`, // Prevents duplicate notifications for same conversation
    requireInteraction: false, // Auto-dismiss after a few seconds
    silent: true, // Don't play OS sound (we already have app sounds)
  });

  // Handle click - focus the window and call callback
  notification.onclick = () => {
    window.focus();
    onClickCallback?.();
    notification.close();
  };

  // Auto-close after 5 seconds if user doesn't interact
  setTimeout(() => {
    notification.close();
  }, 5000);

  return notification;
}

/**
 * Check if we should show OS notifications based on current state
 */
export function shouldShowOSNotification(): boolean {
  return (
    isNotificationSupported() &&
    getNotificationPermission() === 'granted' &&
    !document.hasFocus()
  );
}
